import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const WellnessJourney = ({ user, onDataChange }) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('30days');
  const [journalEntry, setJournalEntry] = useState('');
  
  // Mock data - in real app this would come from API
  const journeyData = {
    energyLevels: {
      current: user?.energyLevel || 7.5,
      trend: '+1.2',
      history: [6.0, 6.5, 7.2, 7.8, 7.5, 8.0, 7.5] // Last 7 days
    },
    meditationProgress: {
      streak: user?.meditationStreak || 15,
      totalMinutes: 450,
      averageSession: 30,
      consistency: 85
    },
    transformationGoals: [
      {
        id: 1,
        title: 'Reduce Daily Stress',
        progress: 75,
        target: '8/10 peaceful days per week',
        current: '6/10 days achieved',
        color: 'emerald'
      },
      {
        id: 2,
        title: 'Increase Energy Levels',
        progress: 60,
        target: 'Maintain 8+ energy daily',
        current: '7.5 average',
        color: 'blue'
      },
      {
        id: 3,
        title: 'Improve Sleep Quality',
        progress: 40,
        target: '8 hours quality sleep',
        current: '6.5 hours average',
        color: 'purple'
      }
    ],
    recentSessions: [
      {
        id: 1,
        date: '2024-01-20',
        type: 'EES Session',
        center: 'Harmony Wellness Center',
        duration: 90,
        energyBefore: 6,
        energyAfter: 8,
        notes: 'Felt deeply relaxed and energized'
      },
      {
        id: 2,
        date: '2024-01-18',
        type: 'Meditation',
        center: 'Zenith Retreat',
        duration: 45,
        energyBefore: 5,
        energyAfter: 7,
        notes: 'Great mindfulness session'
      },
      {
        id: 3,
        date: '2024-01-15',
        type: 'Sound Healing',
        center: 'Sacred Sound Studio',
        duration: 60,
        energyBefore: 4,
        energyAfter: 8,
        notes: 'Profound experience with crystal bowls'
      }
    ],
    achievements: [
      { id: 1, title: '15-Day Meditation Streak', icon: 'Award', unlocked: true, date: '2024-01-20' },
      { id: 2, title: 'Energy Enhancement Master', icon: 'Zap', unlocked: true, date: '2024-01-18' },
      { id: 3, title: 'Mindfulness Practitioner', icon: 'Brain', unlocked: true, date: '2024-01-10' },
      { id: 4, title: '30-Day Consistency', icon: 'Target', unlocked: false },
      { id: 5, title: 'Wellness Community Member', icon: 'Users', unlocked: false }
    ]
  };

  const handleJournalSubmit = () => {
    if (journalEntry.trim()) {
      // In real app, save journal entry
      console.log('Saving journal entry:', journalEntry);
      setJournalEntry('');
      onDataChange?.();
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 75) return 'emerald';
    if (progress >= 50) return 'blue';
    if (progress >= 25) return 'yellow';
    return 'red';
  };

  return (
    <div className="space-y-8">
      {/* Energy Level Tracking */}
      <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
        <div className="px-6 py-4 border-b border-blue-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-slate-800 font-heading flex items-center">
              <Icon name="Activity" size={20} className="mr-2 text-blue-600" />
              Energy Level Tracking
            </h2>
            <select
              value={selectedTimeframe}
              onChange={(e) => setSelectedTimeframe(e.target.value)}
              className="text-sm border border-blue-200 rounded-md px-3 py-1 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="7days">Last 7 days</option>
              <option value="30days">Last 30 days</option>
              <option value="90days">Last 90 days</option>
            </select>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-2xl font-bold text-blue-600 mb-1">
                {journeyData.energyLevels.current}
              </div>
              <div className="text-sm text-slate-600 mb-1">Current Energy</div>
              <div className="text-xs text-emerald-600 flex items-center justify-center">
                <Icon name="TrendingUp" size={12} className="mr-1" />
                {journeyData.energyLevels.trend} this week
              </div>
            </div>
            
            <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="text-2xl font-bold text-emerald-600 mb-1">
                {journeyData.meditationProgress.streak}
              </div>
              <div className="text-sm text-slate-600 mb-1">Day Streak</div>
              <div className="text-xs text-emerald-600">Keep going!</div>
            </div>
            
            <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="text-2xl font-bold text-purple-600 mb-1">
                {journeyData.meditationProgress.totalMinutes}
              </div>
              <div className="text-sm text-slate-600 mb-1">Minutes This Month</div>
              <div className="text-xs text-purple-600">{journeyData.meditationProgress.averageSession} min avg</div>
            </div>
          </div>
          
          {/* Simple Energy Chart Visualization */}
          <div className="bg-white rounded-lg p-4 border border-blue-200">
            <h3 className="text-sm font-medium text-slate-700 mb-4">Energy Levels (Last 7 days)</h3>
            <div className="flex items-end space-x-2 h-24">
              {journeyData.energyLevels.history.map((level, index) => (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <div
                    className="w-full bg-blue-500 rounded-t"
                    style={{ height: `${(level / 10) * 100}%`, minHeight: '4px' }}
                  ></div>
                  <span className="text-xs text-slate-500 mt-1">{level}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Transformation Goals */}
      <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
        <div className="px-6 py-4 border-b border-blue-100">
          <h2 className="text-xl font-semibold text-slate-800 font-heading flex items-center">
            <Icon name="Target" size={20} className="mr-2 text-blue-600" />
            Transformation Goals
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Track progress toward your personal wellness objectives
          </p>
        </div>
        
        <div className="p-6">
          <div className="space-y-4">
            {journeyData.transformationGoals.map((goal) => (
              <div key={goal.id} className="bg-white rounded-lg p-4 border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-slate-800">{goal.title}</h3>
                  <span className="text-sm font-medium text-slate-600">{goal.progress}%</span>
                </div>
                
                <div className="mb-3">
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full bg-${goal.color}-500 transition-all duration-500`}
                      style={{ width: `${goal.progress}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm text-slate-600">
                  <span>Target: {goal.target}</span>
                  <span>Current: {goal.current}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Sessions & Achievements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Sessions */}
        <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
          <div className="px-6 py-4 border-b border-blue-100">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center">
              <Icon name="Calendar" size={18} className="mr-2 text-blue-600" />
              Recent Sessions
            </h3>
          </div>
          
          <div className="p-6">
            <div className="space-y-4">
              {journeyData.recentSessions.map((session) => (
                <div key={session.id} className="bg-white rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-slate-800">{session.type}</h4>
                    <span className="text-xs text-slate-500">{new Date(session.date).toLocaleDateString()}</span>
                  </div>
                  
                  <p className="text-sm text-slate-600 mb-2">{session.center}</p>
                  
                  <div className="flex items-center space-x-4 text-sm text-slate-600 mb-2">
                    <span>{session.duration} min</span>
                    <span className="flex items-center">
                      Energy: {session.energyBefore} → {session.energyAfter}
                      <Icon name="TrendingUp" size={12} className="ml-1 text-emerald-500" />
                    </span>
                  </div>
                  
                  {session.notes && (
                    <p className="text-sm text-slate-600 italic">"{session.notes}"</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Achievements */}
        <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
          <div className="px-6 py-4 border-b border-blue-100">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center">
              <Icon name="Award" size={18} className="mr-2 text-blue-600" />
              Achievements
            </h3>
          </div>
          
          <div className="p-6">
            <div className="space-y-3">
              {journeyData.achievements.map((achievement) => (
                <div
                  key={achievement.id}
                  className={`flex items-center space-x-3 p-3 rounded-lg border-2 transition-all duration-200 ${
                    achievement.unlocked
                      ? 'border-emerald-200 bg-emerald-50' :'border-slate-200 bg-slate-50'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    achievement.unlocked ? 'bg-emerald-500' : 'bg-slate-300'
                  }`}>
                    <Icon 
                      name={achievement.icon} 
                      size={18} 
                      className={achievement.unlocked ? 'text-white' : 'text-slate-500'} 
                    />
                  </div>
                  
                  <div className="flex-1">
                    <h4 className={`font-medium ${
                      achievement.unlocked ? 'text-emerald-800' : 'text-slate-600'
                    }`}>
                      {achievement.title}
                    </h4>
                    {achievement.date && (
                      <p className="text-xs text-emerald-600">
                        Unlocked {new Date(achievement.date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  
                  {achievement.unlocked && (
                    <Icon name="Check" size={16} className="text-emerald-500" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Wellness Journal */}
      <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
        <div className="px-6 py-4 border-b border-blue-100">
          <h2 className="text-xl font-semibold text-slate-800 font-heading flex items-center">
            <Icon name="BookOpen" size={20} className="mr-2 text-blue-600" />
            Wellness Journal
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Record your thoughts, insights, and reflections on your wellness journey
          </p>
        </div>
        
        <div className="p-6">
          <div className="mb-4">
            <textarea
              value={journalEntry}
              onChange={(e) => setJournalEntry(e.target.value)}
              className="w-full px-4 py-3 border border-blue-200 rounded-lg resize-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
              rows={4}
              placeholder="How are you feeling today? What insights did you gain from your sessions? Record your wellness journey..."
            />
          </div>
          
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">
              Today • {new Date().toLocaleDateString()}
            </p>
            <button
              onClick={handleJournalSubmit}
              disabled={!journalEntry.trim()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors duration-200"
            >
              Save Entry
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WellnessJourney;